/**
 * @fileOverview Render a table with filters and a search. Requires an
 * asynchronous data source for the table data. Makes assumptions about
 * a rough convention that is followed. Useful if you want to render data
 * from a graphQL endpoint with minimal boilerplate.
 */

import React from 'react'
import { SORT_DIRECTION } from './../TableTable/types'
import TableTable from './../TableTable'
import DataFetchingComponent from './../DataFetchingComponent'

/**
 * @typedef FetchDataArgs
 * @property {number} first How many records it is requesting to return. So if
 * this value is 20 it expects a result returned with 20 records.
 * @property {number} skip How many records to skip given the current fetch
 * criteria, so if this value is 40, then skip the first 40 records.
 * @property {string} filter Filter search text to filter results by. This is
 * the value the user will have entered into the search field.
 * @property {string} orderBy Depending on which table header was selected this
 * will return the label for that header with spaces removed, uppercased and
 * `_ASC` or `_DESC` appended. For example if the header is `Time of Day`. And
 * that header is clicked, this would return `TIMEOFDAY_ASC`. If it was clicked
 * a second time it would return `TIMEOFDAY_DESC`.
 *
 * @typedef DataFetchingTableTableFilterOption
 * @property {string} label The label to display in the filter option
 * @property {Mixed} value The value to pass back to the fetchData function.
 * Usually a database id but can be anything.
 *
 * @typedef DataFetchingTableTableFilter
 * @property {string} label This is the caption that's displayed next to the
 * filter
 * @property {Array<DataFetchingTableTableFilterOption>} options The filter options
 *
 * @typedef DataFetchingTableTableProps
 * @property {Array<DataFetchingTableTableFilter>} filters The filters options
 * to show against the table.
 * For example
 * ```
 * [
 *   {
 *     label: 'Status',
 *     options: [
 *       { label: 'Draft', value: 'DRAFT' },
 *       { label: 'Available', value: 'AVAILABLE' },
 *     ],
 *   },
 *   {
 *     label: 'Client',
 *     options: [
 *       { label: 'Woolworths', value: 2 },
 *       { label: 'Uber', value: 1, },
 *     ]
 *   }
 * ]
 * ```
 * @property {Array<string>} header An array of values to render in
 * cells for the table header. Must be strings, because these values are used
 * to set the orderBy string. So if `Week` is used the sort order passed to
 * `orderBy` will be either `WEEK_ASC` or `WEEK_DESC`.
 * For example `['CLIENT', 'WEEK', 'DATE', <span>TIME</span>]`
 * @property {Function} fetchData This gets the table's data exactly as it
 * should be rendered. It will be called with the first parameter being an
 * object of type `FetchDataArgs` (see type for more info). Additionally to
 * the arguments specified in the `FetchDataArgs` type it will also include
 * properties that match the filter labels if any value for that filer is
 * selected. e.g. If you had a filter with label 'Client' and the value of the
 * selected option in the filter was the client id then the args object will
 * have the 'Client' property with the client id as the value. Use this to
 * make your filters filter the searched for data. It also must return an
 * object with the props `data` and `totalCount`. You can also return an
 * `actions` array, this should be an array of functions that will be
 * fired when a row is tapped or clicked.
 * For example.
 * ```
 *  var data = await getMissions({
 *    // Passes the first and skip params to our method to limit results
 *    //returned
 *    ...filters,
 *    clientId: filters['Client'],
 *  })
 *  var result = await response.json()
 *  return {
 *    // This `structureData` function will put the various mission properties
 *    // into a multi dimensional array ready for rendering.
 *    data: structureData(data.missions),
 *    // We also need to indicate how many more records there are, you will
 *    // need to make your backend account for this without returning the
 *     // entire data set.
 *    totalCount: data.totalCount
 *  }
 * ```
 * @property {Array<string>} sortableHeaders By default all headers are sortable
 * but if you want to only make some sortable pass this in. The array values
 * must match the table headers.
 * e.g. `['Client', 'Location']
 * @property {boolean} showSearch Default is false
 * @property {function} refetch A function that this table calls on re-render,
 * which triggers a data refetch if it returns a truthy value. This gives
 * subcomponents a method to trigger refetches if the data source changes.
 * @property {boolean} enableFilterSuggestions Passed through to the
 * SearchAndFilters compontnt, allows you to type in the search box to select
 * filter options via auto complete
 *
 * @param {DataFetchingTableTableProps} props
 */
const DataFetchingTableTable = props => {
  return <DataFetchingComponent {...props} Component={WrappedTableTable} />
}

export default DataFetchingTableTable

const WrappedTableTable = props => {
  var {
    header,
    setSelectedSort,
    sortableHeaders,
    selectedSort,
    setOffsetRecordsStart,
    ...otherProps
  } = props

  // Resets the flag the prevents refetching data, useful to use with methods
  // that change data filters
  const bindDataReset = func => (...args) => {
    func(...args)
    // Reset the paging
    setOffsetRecordsStart(0)
  }

  // Map the header to the format the TableTable expects
  const mappedHeader = header.map(label => {
    var upperLabel = label.toUpperCase().replace(/\s+/g, '')
    var sortDetails = { label }
    if (!sortableHeaders || sortableHeaders.includes(label)) {
      sortDetails.sortDirection = (() => {
        switch (selectedSort) {
          case upperLabel + '_ASC':
            return SORT_DIRECTION.ASCENDING
          case upperLabel + '_DESC':
            return SORT_DIRECTION.DECENDING
          default:
            return SORT_DIRECTION.NO_SORT
        }
      })()
      sortDetails.onSortDirectionChange = bindDataReset(() => {
        switch (selectedSort) {
          case upperLabel + '_ASC':
            return setSelectedSort(upperLabel + '_DESC')
          case upperLabel + '_DESC':
            return setSelectedSort(upperLabel + '_ASC')
          default:
            return setSelectedSort(upperLabel + '_ASC')
        }
      })
    }
    return sortDetails
  })

  return (
    <TableTable
      {...otherProps}
      setSelectedSort={setSelectedSort}
      sortableHeaders={sortableHeaders}
      selectedSort={selectedSort}
      header={mappedHeader}
    />
  )
}



// WEBPACK FOOTER //
// ./src/Components/DataFetchingTableTable/index.js